import type { Optional } from '@festina-life/core'
import { useDataContext } from '@festina-life/ui/theme'

import {
  useScenarioContext,
  variantIdentifierToString,
} from '../context/scenarioContext'
import { useHook } from '../lenteHooks'
import { isInCalculationStop } from '../utility/isInStop'
import { useCommitmentVersionSelection } from './useCommitmentVersionSelection'

export type CurrentCommitmentContextHookVariant =
  | 'currentDefaultEvent'
  | 'currentDefaultSimulation'
  | 'currentAppliedCommand'
  | 'currentByCommitmentDate'
  | 'currentFutureCommand'
  | 'currentScenarioSimulation'
  | 'currentNone'

type UseCommitmentContextVariantProps = {
  commitmentDate?: Optional<string>
  currentOverrideCommitmentVariant?: CurrentCommitmentContextHookVariant
}

export function useCurrentCommitmentContextVariant({
  commitmentDate,
  currentOverrideCommitmentVariant,
}: UseCommitmentContextVariantProps = {}): CurrentCommitmentContextHookVariant {
  const { ownerId, tenantId } = useHook('routing.general.useEntityParams')()
  const { previousEventId, expectedApplyDate } = useDataContext()
  const { variantIdentifier } = useScenarioContext()
  const { currentSelection, currentSelectionSimulationDate } =
    useCommitmentVersionSelection()

  const scenarioIdForRequestHeader = variantIdentifier
    ? variantIdentifierToString(variantIdentifier)
    : undefined

  const { data: hasCalculationStop } = useHook(
    'fetchers.life.useFetchCommitmentSourcesStopsLatest',
  )(
    {
      ownerId,
      tenantId,
      scenarioId: scenarioIdForRequestHeader,
    },
    {
      select: (stopData) => isInCalculationStop(stopData?.stop),
    },
  )

  const isFutureAndStopped = !!expectedApplyDate && hasCalculationStop

  if (currentOverrideCommitmentVariant) {
    return currentOverrideCommitmentVariant
  }

  if (scenarioIdForRequestHeader) {
    return 'currentScenarioSimulation'
  }

  if (commitmentDate) {
    return 'currentByCommitmentDate'
  } else if (previousEventId) {
    return 'currentAppliedCommand'
  } else if (expectedApplyDate && !isFutureAndStopped) {
    return 'currentFutureCommand'
  } else if (currentSelection?.type === 'event') {
    return 'currentDefaultEvent'
  } else if (
    currentSelection?.type === 'unappliedCommand' &&
    !!currentSelectionSimulationDate &&
    !isFutureAndStopped
  ) {
    return 'currentDefaultSimulation'
  }

  return 'currentNone'
}
